import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { tryFetchWithAuth } from 'utils/fetchWithAuth';

const CreditsContext = createContext({
    balance: 0,
    purchasedTotal: 0,
    planId: null,
    isUnlimited: false,
    recentActivity: [],
    loading: false,
    error: null,
    refresh: async () => {},
    applyDelta: () => {},
});

export function CreditsProvider({ children }) {
    const { user, session } = useAuth();
    const [balance, setBalance] = useState(0);
    const [purchasedTotal, setPurchasedTotal] = useState(0);
    const [planId, setPlanId] = useState(null);
    const [isUnlimited, setIsUnlimited] = useState(false);
    const [recentActivity, setRecentActivity] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        setError(null);
        const { data, error: fetchError } = await tryFetchWithAuth('/credits/balance');
        if (fetchError) {
            setError(fetchError);
        } else if (data) {
            setBalance(data.balance ?? 0);
            setPurchasedTotal(data.purchasedTotal ?? 0);
            setPlanId(data.planId ?? null);
            setIsUnlimited(!!data.isUnlimited);
            setRecentActivity(Array.isArray(data.recentActivity) ? data.recentActivity : []);
        }
        setLoading(false);
    }, [user]);

    useEffect(() => {
        if (session && user) {
            refresh();
        } else {
            setBalance(0);
            setPurchasedTotal(0);
            setPlanId(null);
            setIsUnlimited(false);
            setRecentActivity([]);
        }
    }, [session, user, refresh]);

    // Optimistic local update after an unlock succeeds so the navbar widget reflects the spend immediately.
    const applyDelta = useCallback((delta) => {
        setBalance(prev => Math.max(0, prev + delta));
    }, []);

    const value = useMemo(() => ({
        balance,
        purchasedTotal,
        planId,
        isUnlimited,
        recentActivity,
        loading,
        error,
        refresh,
        applyDelta,
    }), [balance, purchasedTotal, planId, isUnlimited, recentActivity, loading, error, refresh, applyDelta]);

    return <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>;
}

export function useCredits() {
    return useContext(CreditsContext);
}
